import { Controller } from "@hotwired/stimulus"

// The Non-contacts assign panel: progressive identity disclosure after a
// client is picked, and the native cross-client duplicate modal. The
// client is picked through THE client control (shared/_client_picker),
// which dispatches client-picker:change; this controller only reacts. All
// matching TRUTH lives server-side (the controller re-checks on submit).
export default class extends Controller {
  static targets = ["form", "confirmedIds", "identity", "person", "name", "role", "roleOther",
                    "roleWrap", "clientCardLabel", "resolvedNote", "submit", "modal"]
  static values = { selectedId: String, hasConflict: Boolean }

  connect() {
    this.clientId = ""
    // A 422 re-render restores the picked client (the picker restores its
    // own field silently, so the identity step is re-revealed here) and,
    // when the server found a cross-client conflict, opens the modal.
    if (this.selectedIdValue) {
      const item = this.element.querySelector(`[data-client-picker-target='item'][data-client-id='${this.selectedIdValue}']`)
      if (item) this.select(item, { keepFields: true })
    }
    if (this.hasConflictValue && this.hasModalTarget) this.openModal()
  }

  picked(event) {
    const { id, element } = event.detail || {}
    if (id && element) this.select(element)
    else this.unselect()
  }

  select(item, { keepFields = false } = {}) {
    this.clientId = item.dataset.clientId
    this.clientName = item.dataset.name
    this.existingName = item.dataset.existingName || null
    this.existingRole = item.dataset.existingRole || null

    if (this.existingName) {
      // Pre-resolved: the client already has this number saved; no identity
      // step, no merge ambiguity.
      this.identityTarget.classList.add("hidden")
      this.resolvedNoteTarget.classList.remove("hidden")
      const role = this.existingRole ? ` (${this.existingRole})` : ""
      this.resolvedNoteTarget.textContent =
        `This number is already saved for ${this.clientName} as ${this.existingName}${role}. Assigning moves these texts into that conversation.`
    } else {
      this.resolvedNoteTarget.classList.add("hidden")
      this.identityTarget.classList.remove("hidden")
      this.clientCardLabelTarget.textContent = `This is ${this.clientName}`
      if (!keepFields) {
        this.identityRadios().forEach((r) => (r.checked = false))
        this.personTarget.classList.add("hidden")
      } else {
        this.identityChanged()
      }
    }
    this.validate()
  }

  unselect() {
    this.clientId = ""
    this.clientName = null
    this.existingName = null
    this.identityTarget.classList.add("hidden")
    this.resolvedNoteTarget.classList.add("hidden")
    this.validate()
  }

  identityChanged() {
    const choice = this.identityValue()
    if (choice === "client") {
      this.personTarget.classList.remove("hidden")
      this.roleWrapTarget.classList.add("hidden")
      // Pre-fill, never lock: a "client" is often a band and the texter one
      // of its people.
      if (this.nameTarget.value.trim() === "") this.nameTarget.value = this.clientName || ""
    } else if (choice === "team") {
      this.personTarget.classList.remove("hidden")
      this.roleWrapTarget.classList.remove("hidden")
      if (this.nameTarget.value.trim() === (this.clientName || "")) this.nameTarget.value = ""
    }
    this.validate()
  }

  roleChanged() {
    this.roleOtherTarget.classList.toggle("hidden", this.roleTarget.value !== "Other")
  }

  validate() {
    const clientPicked = this.clientId !== ""
    const resolved = !!this.existingName
    const identityOk = resolved || (this.identityValue() && this.nameTarget.value.trim() !== "")
    this.submitTarget.disabled = !(clientPicked && identityOk)
    if (clientPicked) {
      this.submitTarget.textContent = resolved
        ? `Move texts to ${this.clientName}`
        : `Assign to ${this.clientName}`
    } else {
      this.submitTarget.textContent = "Move texts"
    }
  }

  submitting() {
    if (this.submitTarget.disabled) return
    // Disable AFTER this click submits (next tick) so the form still sends.
    setTimeout(() => {
      this.submitTarget.disabled = true
      this.submitTarget.textContent = "Moving..."
    }, 0)
  }

  openModal() {
    this.modalTarget.hidden = false
    this.previousFocus = document.activeElement
    const confirm = this.modalTarget.querySelector("[data-conflict-ids]")
    if (confirm) confirm.focus()
    this.trapHandler = (e) => this.trapFocus(e)
    this.modalTarget.addEventListener("keydown", this.trapHandler)
  }

  closeModal() {
    this.modalTarget.hidden = true
    this.modalTarget.removeEventListener("keydown", this.trapHandler)
    if (this.previousFocus) this.previousFocus.focus()
  }

  confirmConflict(event) {
    this.confirmedIdsTarget.value = event.currentTarget.dataset.conflictIds
    this.closeModal()
    this.submitTarget.disabled = false
    this.formTarget.requestSubmit()
    // requestSubmit skips the click-bound submitting(); show progress here.
    this.submitTarget.disabled = true
    this.submitTarget.textContent = "Moving..."
  }

  trapFocus(event) {
    if (event.key === "Escape") return this.closeModal()
    if (event.key !== "Tab") return
    const focusable = this.modalTarget.querySelectorAll("button")
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus()
    }
  }

  identityValue() {
    const checked = this.identityRadios().find((r) => r.checked)
    return checked ? checked.value : null
  }

  identityRadios() {
    return Array.from(this.element.querySelectorAll('input[name="identity"]'))
  }
}
